import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import PortalDomainSelect from "./PortalDomainSelect";
import { canDeleteInPortal } from "./portalSession";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";

const CLIENT_STATUSES = ["Active", "Inactive"];
const EMPTY_FORM = {
  clientName: "",
  contactPerson: "",
  mobileNumber: "",
  altNumber: "",
  email: "",
  address: "",
  website: "",
  domain: "",
  description: "",
  status: "Active",
};

function display(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value);
}

function servicesSummary(services) {
  if (!Array.isArray(services) || services.length === 0) return "—";
  const names = services
    .filter((s) => s.Status === "Active")
    .map((s) => s.Service_Name)
    .filter(Boolean);
  return names.length ? names.join(", ") : "—";
}

function projectsSummary(projects) {
  if (!Array.isArray(projects) || projects.length === 0) return "—";
  const names = projects
    .filter((p) => p.Status === "Active")
    .map((p) => {
      if (p.Short_Code && p.Project_Name) return `${p.Short_Code} (${p.Project_Name})`;
      return p.Short_Code || p.Project_Name;
    })
    .filter(Boolean);
  return names.length ? names.join(", ") : "—";
}

const SORT_ACCESSORS = {
  clientName: (c) => (c.Client_Name || "").toLowerCase(),
  contactPerson: (c) => (c.Contact_Person || "").toLowerCase(),
  mobileNumber: (c) => (c.Mobile_Number || "").toLowerCase(),
  email: (c) => (c.Email || "").toLowerCase(),
  domain: (c) => (c.Domain || "").toLowerCase(),
  status: (c) => (c.Status || "").toLowerCase(),
  projects: (c) => projectsSummary(c.Projects).toLowerCase(),
  services: (c) => servicesSummary(c.Services).toLowerCase(),
};

const COLUMNS = [
  { key: "clientName", label: "Client name" },
  { key: "contactPerson", label: "Contact person" },
  { key: "mobileNumber", label: "Mobile" },
  { key: "email", label: "Email" },
  { key: "domain", label: "Business" },
  { key: "projects", label: "Projects" },
  { key: "services", label: "Services" },
  { key: "status", label: "Status" },
];

const DATE_PRESETS = [
  { id: "all", label: "All time" },
  { id: "today", label: "Today" },
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "month", label: "This month" },
  { id: "custom", label: "Custom" },
];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function presetRange(preset, fromDate, toDate) {
  const now = new Date();
  switch (preset) {
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "7": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 6);
      return { from, to: endOfDay(now) };
    }
    case "30": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 29);
      return { from, to: endOfDay(now) };
    }
    case "month":
      return { from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: endOfDay(now) };
    case "custom":
      return {
        from: fromDate ? startOfDay(new Date(fromDate)) : null,
        to: toDate ? endOfDay(new Date(toDate)) : null,
      };
    default:
      return { from: null, to: null };
  }
}

function CustomerTenantClients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("clientName");
  const [sortDir, setSortDir] = useState("asc");
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [search, setSearch] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deactivatingId, setDeactivatingId] = useState(null);
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const loadClients = useCallback(async () => {
    const res = await axiosInstance.get("/client/tenant-clients");
    setClients(Array.isArray(res.data?.data) ? res.data.data : []);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await loadClients();
      } catch (_) {
        if (active) setError("Could not load clients.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadClients]);

  const filtered = useMemo(() => {
    let list = clients;

    const { from, to } = presetRange(datePreset, fromDate, toDate);
    if (from || to) {
      list = list.filter((c) => {
        if (!c.Created_On) return false;
        const created = new Date(c.Created_On);
        if (Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
        return true;
      });
    }

    if (statusFilter) list = list.filter((c) => c.Status === statusFilter);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((c) =>
        [
          c.Client_Name,
          c.Contact_Person,
          c.Mobile_Number,
          c.Alt_Number,
          c.Email,
          c.Address,
          c.Website,
          c.Domain,
          c.Description,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }

    return list;
  }, [clients, search, datePreset, fromDate, toDate, statusFilter]);

  const hasActiveFilters = datePreset !== "all" || !!statusFilter || !!search;

  const clearFilters = () => {
    setDatePreset("all");
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setSearch("");
  };

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const closeAddModal = () => {
    setShowAddModal(false);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const buildPayload = () => ({
    Client_Name: form.clientName.trim(),
    Contact_Person: form.contactPerson.trim() || null,
    Mobile_Number: form.mobileNumber.trim() || null,
    Alt_Number: form.altNumber.trim() || null,
    Email: form.email.trim() || null,
    Address: form.address.trim() || null,
    Website: form.website.trim() || null,
    Domain: form.domain.trim() || null,
    Description: form.description.trim() || null,
    Status: form.status || "Active",
  });

  const handleDeactivate = async (client) => {
    if (client.Status === "Inactive") return;
    if (!window.confirm(`Deactivate ${client.Client_Name}? Their status will be set to Inactive.`)) return;

    setDeactivatingId(client.Tenant_Client_Id);
    setError("");
    try {
      await axiosInstance.delete(`/client/tenant-clients/${client.Tenant_Client_Id}`);
      await loadClients();
    } catch (err) {
      setError(err.response?.data?.message || "Could not deactivate client.");
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clientName.trim()) {
      setFormError("Client name is required.");
      return;
    }
    if (!form.contactPerson.trim()) {
      setFormError("Contact person is required.");
      return;
    }

    const mobileError = mobileValidationMessage(form.mobileNumber, {
      required: true,
      label: "Mobile number",
    });
    if (mobileError) {
      setFormError(mobileError);
      return;
    }
    const altNumberError = mobileValidationMessage(form.altNumber, { label: "Alternate number" });
    if (altNumberError) {
      setFormError(altNumberError);
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      await axiosInstance.post("/client/tenant-clients", buildPayload());
      await loadClients();
      closeAddModal();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not create client."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cportal-page">
      <div className="cportal-page-head">
        <h1>Clients</h1>
        <button
          type="button"
          className="cportal-btn cportal-btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          Add client
        </button>
      </div>

      {error && <div className="cportal-alert cportal-alert-error">{error}</div>}

      <div className="cportal-filters">
        <div className="cportal-filter-presets">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`cportal-chip ${datePreset === p.id ? "active" : ""}`}
              onClick={() => setDatePreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {datePreset === "custom" && (
          <div className="cportal-filter-dates">
            <label className="cportal-field">
              <span>From date</span>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label className="cportal-field">
              <span>To date</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
          </div>
        )}

        <div className="cportal-filter-row">
          <input
            className="cportal-search cportal-filter-search"
            type="text"
            placeholder="Search client, contact, phone, email, business"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All status</option>
            {CLIENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {hasActiveFilters && (
            <button type="button" className="cportal-clear-filters" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="cportal-card">
        <div className="cportal-card-head">
          <span className="cportal-muted">{sorted.length} client(s)</span>
        </div>

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">
            {clients.length === 0 ? "No clients added yet." : "No clients match your filters."}
          </p>
        ) : (
          <div className="cportal-table-wrap">
            <table className="cportal-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((client) => (
                  <tr key={client.Tenant_Client_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() =>
                          navigate(`/portal/clients/${client.Tenant_Client_Id}`)
                        }
                      >
                        {display(client.Client_Name)}
                      </button>
                    </td>
                    <td>{display(client.Contact_Person)}</td>
                    <td>{display(client.Mobile_Number)}</td>
                    <td>{display(client.Email)}</td>
                    <td>{display(client.Domain)}</td>
                    <td>{projectsSummary(client.Projects)}</td>
                    <td>{servicesSummary(client.Services)}</td>
                    <td>{display(client.Status)}</td>
                    <td>
                      <div className="cportal-re-actions">
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--edit"
                          onClick={() =>
                            navigate(`/portal/clients/${client.Tenant_Client_Id}`, {
                              state: { edit: true },
                            })
                          }
                          title="Edit client"
                          aria-label="Edit client"
                        >
                          <FaEdit size={16} />
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="re-lead-action re-lead-action--delete"
                            onClick={() => handleDeactivate(client)}
                            disabled={
                              client.Status === "Inactive" ||
                              deactivatingId === client.Tenant_Client_Id
                            }
                            title={
                              client.Status === "Inactive"
                                ? "Already inactive"
                                : "Deactivate client"
                            }
                            aria-label="Deactivate client"
                          >
                            {deactivatingId === client.Tenant_Client_Id ? "…" : <FaTrash size={16} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="cportal-modal-overlay" onClick={closeAddModal} role="presentation">
          <div
            className="cportal-modal cportal-modal--form cportal-modal--wide"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="add-tenant-client-title"
          >
            <div className="cportal-modal-header">
              <h3 id="add-tenant-client-title">Add client</h3>
              <button type="button" onClick={closeAddModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form className="cportal-modal-body" onSubmit={handleSubmit}>
              {formError && (
                <div className="cportal-alert cportal-alert-error">{formError}</div>
              )}
              <div className="cportal-form-grid cportal-form-grid--3">
                <div className="cportal-field">
                  <label>
                    Client name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.clientName}
                    onChange={(e) => setField("clientName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Contact person <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.contactPerson}
                    onChange={(e) => setField("contactPerson", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Mobile number <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.mobileNumber}
                    onChange={(e) => setField("mobileNumber", sanitizeMobileInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>Alt number</label>
                  <input
                    value={form.altNumber}
                    onChange={(e) => setField("altNumber", sanitizeMobileInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                  />
                </div>
                <div className="cportal-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Website</label>
                  <input
                    value={form.website}
                    onChange={(e) => setField("website", e.target.value)}
                    placeholder="https://"
                  />
                </div>
                <PortalDomainSelect value={form.domain} onChange={(value) => setField("domain", value)} />
                <div className="cportal-field">
                  <label>
                    Status <span className="cportal-required">*</span>
                  </label>
                  <select value={form.status} onChange={(e) => setField("status", e.target.value)}>
                    {CLIENT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="cportal-form-grid cportal-form-grid--3">
                <div className="cportal-field cportal-field--full">
                  <label>Address</label>
                  <textarea
                    rows={3}
                    value={form.address}
                    onChange={(e) => setField("address", e.target.value)}
                  />
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </div>
              </div>

              <div className="cportal-form-actions">
                <button
                  type="button"
                  className="cportal-btn cportal-btn-ghost"
                  onClick={closeAddModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="cportal-btn cportal-btn-primary" disabled={submitting}>
                  {submitting ? "Creating…" : "Create client"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerTenantClients;
